import { useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
} from '@heroui/react';
import type { ClienteRequest } from '@/types/cliente.model';

interface ClienteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ClienteRequest['data']) => Promise<void>;
  loading?: boolean;
}

const ClienteModal = ({ isOpen, onClose, onSubmit, loading }: ClienteModalProps) => {
  const [formData, setFormData] = useState<Omit<ClienteRequest['data'], 'tenantId' | 'locale' | 'localizations'>>({
    direccion: '',
    email: '',
    emailContacto: '',
    nombre: '',
    numeroIdentificacion: '',
    personaContacto: '',
    razonSocial: '',
    telefono: '',
    telefonoContacto: '',
    tipoIdentificacion: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const documentTypes = [
    { key: 'CC', label: 'Cédula de Ciudadanía' },
    { key: 'CE', label: 'Cédula de Extranjería' },
    { key: 'PA', label: 'Pasaporte Colombiano' },
    { key: 'PAE', label: 'Pasaporte Extranjero' },
    { key: 'PPT', label: 'Permiso por Protección Temporal' },
    { key: 'SC', label: 'Salvoconducto de Permanencia o de Salida' },
  ];

  const getDocumentTypeLength = (documentType: string): number => {
    switch (documentType) {
      case 'CC':
        return 10; // Cédula de Ciudadanía: up to 10 digits
      case 'CE':
        return 12; // Cédula de Extranjería: up to 12 digits
      case 'PA':
        return 10; // Pasaporte Colombiano: up to 10 characters
      case 'PAE':
        return 10; // Pasaporte Extranjero: up to 10 characters
      case 'PPT':
        return 15; // Permiso por Protección Temporal: up to 15 characters
      case 'SC':
        return 15; // Salvoconducto: up to 15 characters
      default:
        return 20; // Default max length
    }
  };

  const handleChange = (name: string, value: string) => {
    // If changing the document type, adjust the number field accordingly
    if (name === 'tipoIdentificacion') {
      setFormData({
        ...formData,
        [name]: value,        
        // Clear the identification number when changing document type
        numeroIdentificacion: '',
      });
      console.log(name, value)
    } else {
      // Limit the number based on document type
      if (name === 'numeroIdentificacion' && formData.tipoIdentificacion) {
        const maxLength = getDocumentTypeLength(formData.tipoIdentificacion);
        if (value.length > maxLength) {
          return; // Don't update if exceeds max length
        }
      }
      
      setFormData({
        ...formData,
        [name]: value,
      });
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const handleSubmit = async () => {
    // Basic validation
    const newErrors: Record<string, string> = {};
    if (!formData.nombre.trim()) newErrors.nombre = 'Nombre es requerido';
    if (!formData.numeroIdentificacion.trim()) newErrors.numeroIdentificacion = 'Número de identificación es requerido';
    if (!formData.tipoIdentificacion.trim()) newErrors.tipoIdentificacion = 'Tipo de identificación es requerido';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await onSubmit(formData);
      // Clear form data after successful submission
      setFormData({
        direccion: '',
        email: '',
        emailContacto: '',
        nombre: '',
        numeroIdentificacion: '',
        personaContacto: '',
        razonSocial: '',
        telefono: '',
        telefonoContacto: '',
        tipoIdentificacion: '',
      });
      // Clear any existing errors
      setErrors({});
      // Close the modal after successful submission
      onClose();
    } catch (error) {
      // Optionally handle submission errors here
      console.error('Error submitting form:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e.target.name, e.target.value);
  };

   const handleSelectionChange = (e: any) => {
    handleChange('tipoIdentificacion', e.currentKey.toString());
  };

  const handleNumeroIdentificacionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow alphanumeric characters, hyphens, and spaces
    if (formData.tipoIdentificacion) {
      const maxLength = getDocumentTypeLength(formData.tipoIdentificacion);
      if (value.length <= maxLength) {
        handleChange('numeroIdentificacion', value);
      }
    } else {
      handleChange('numeroIdentificacion', value);
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      placement="top-center"
      backdrop="blur"
      size="2xl"
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">Crear Nuevo Cliente</ModalHeader>
            <ModalBody className="space-y-4">
                <hr/>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  variant="bordered"
                  isRequired
                  errorMessage={errors.nombre}
                />
                
                <Input
                  label="Razón Social"
                  name="razonSocial"
                  value={formData.razonSocial || ''}
                  onChange={handleInputChange}
                  variant="bordered"
                />
                <Select                  
                  className="max-w-xs"
                  defaultSelectedKeys={['']}
                  label="Tipo de documento"
                  variant="bordered"    
                  placeholder="Seleccione un tipo de documento"
                  onSelectionChange={handleSelectionChange}
                >
                  {documentTypes.map((documento) => (
                    <SelectItem key={documento.key}>{documento.label}</SelectItem>
                  ))}
                </Select>
                
                <Input
                  label="Número de Identificación"
                  name="numeroIdentificacion"
                  value={formData.numeroIdentificacion}
                  onChange={handleNumeroIdentificacionChange}
                  variant="bordered"
                  isRequired
                  errorMessage={errors.numeroIdentificacion}
                  maxLength={formData.tipoIdentificacion ? getDocumentTypeLength(formData.tipoIdentificacion) : undefined}
                />
                
                <Input
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email || ''}
                  onChange={handleInputChange}
                  variant="bordered"
                  errorMessage={errors.email}
                />
                
                <Input
                  label="Teléfono"
                  name="telefono"
                  value={formData.telefono || ''}
                  onChange={handleInputChange}
                  variant="bordered"
                  errorMessage={errors.telefono}
                />
                
                <Input
                  label="Email de Contacto"
                  name="emailContacto"
                  type="email"
                  value={formData.emailContacto || ''}
                  onChange={handleInputChange}
                  variant="bordered"
                  errorMessage={errors.emailContacto}
                />
                
                <Input
                  label="Persona de Contacto"
                  name="personaContacto"
                  value={formData.personaContacto || ''}
                  onChange={handleInputChange}
                  variant="bordered"
                  errorMessage={errors.personaContacto}
                />
                
                <Input
                  label="Teléfono de Contacto"
                  name="telefonoContacto"
                  value={formData.telefonoContacto || ''}
                  onChange={handleInputChange}
                  variant="bordered"
                  errorMessage={errors.telefonoContacto}
                />
                
                <Input
                  label="Dirección"
                  name="direccion"
                  value={formData.direccion || ''}
                  onChange={handleInputChange}
                  variant="bordered"
                  errorMessage={errors.direccion}
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button 
                color="danger" 
                variant="light" 
                onPress={onClose}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button 
                color="primary" 
                onPress={handleSubmit}
                isLoading={loading}
              >
                Guardar Cliente
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ClienteModal;